import { useState } from "react";
import { Box, Button, PasswordInput, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "mantine-form-yup-resolver";
import { supabaseClient } from "supabase/supabase";
import { useAppTranslation } from "hooks/useTranslation";

export function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const schema = yup.object().shape({
    password: yup
      .string()
      .min(6, t("auth.password_min_length"))
      .required(t("auth.password_required")),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password")], t("auth.passwords_must_match"))
      .required(t("auth.confirm_password_required")),
  });

  const form = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: yupResolver(schema),
  });

  const handleSubmit = async (values: typeof form.values) => {
    try {
      setIsLoading(true);
      setError("");

      const { error: updateError } = await supabaseClient.auth.updateUser({
        password: values.password,
      });

      if (updateError) throw updateError;

      // Redirect to login page after successful password reset
      navigate("/login");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box maw={400} mx="auto" mt={50}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Text size="xl" fw={500} ta="center">
            {t("auth.reset_password")}
          </Text>

          {error && (
            <Text c="red" size="sm" ta="center">
              {error}
            </Text>
          )}

          <PasswordInput
            label={t("auth.new_password")}
            placeholder={t("auth.enter_new_password")}
            {...form.getInputProps("password")}
          />

          <PasswordInput
            label={t("auth.confirm_password")}
            placeholder={t("auth.confirm_new_password")}
            {...form.getInputProps("confirmPassword")}
          />

          <Button type="submit" loading={isLoading}>
            {t("auth.update_password")}
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
